import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Loader, Text } from '@mantine/core';
import { DocumentTable } from '@dealstack/frontend/shared/components/Documents/DocumentTable/DocumentTable';
import { EidEasySigningModal } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { MAX_BULK_SIGN_BATCH_SIZE } from '@dealstack/shared/lib/common/eideasy/bulkSigning';
import { isForbiddenApiError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useProgrammeSigningDocuments } from './useProgrammeSigningDocuments';
import {
	PROGRAMME_SIGNING_DOCUMENT_QUERY_KEY,
	useProgrammeSigningDocumentPreview,
} from './useProgrammeSigningDocumentPreview';
import { PUBLIC_BULK_ALLOWED_METHODS, usePublicBulkEidEasySign } from './usePublicBulkEidEasySign';
import { usePublicSignatureTokenExchange } from './usePublicSignatureTokenExchange';
import { createProgrammeSigningColumns } from './createProgrammeSigningColumns';
import { useProgrammeSigningSelection } from './useProgrammeSigningSelection';
import { buildDocumentSlotRoles, groupSignatureRowsByDocument } from './ProgrammeSigningPage.utils';
import { PublicSigningHeader } from './PublicSigningHeader';
import { ProgrammeSigningCancelledScreen } from './ProgrammeSigningCancelledScreen';
import { StatusScreen } from './PublicSigningPage.styles';
import type { ProgrammeSigningDocumentRow } from './ProgrammeSigningPage.types';

// Lazy for the same reason PublicSigningPage lazy-loads DocumentViewerV2: the
// viewer drags in the react-pdf chunk, which most visits never need.
const DocumentViewerModalV2 = lazy(() =>
	import('@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerModalV2').then(
		(m) => ({ default: m.DocumentViewerModalV2 })
	)
);

const buildSingleDocPath = (perSignatureToken: string) =>
	`/public-signing?token=${encodeURIComponent(perSignatureToken)}`;

const PROGRAMME_SIGNING_DOCUMENTS_QUERY_KEY = (token: string) => [
	'programme-signing-documents',
	token,
];

/**
 * The authorised-signatory portal page: reads a programme-scoped public
 * signing token from the query string, lists that person's documents in the
 * programme, and drives bulk signing (with single-doc and per-row fallbacks
 * onto the existing single-document public signing flow, which requires a
 * signature-scoped token — see `usePublicSignatureTokenExchange`).
 */
export const ProgrammeSigningPage = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const tMipManage = useTranslationMipManage();
	const token = searchParams.get('token') ?? '';

	const { data, isLoading, isError } = useProgrammeSigningDocuments(token);
	const tokenExchange = usePublicSignatureTokenExchange(token);

	// Grouped, not raw: the nesting below indents a slot under the row above it, which is only
	// truthful once a document's rows are known to sit together. The API does not promise that.
	const documents = useMemo<ProgrammeSigningDocumentRow[]>(
		() => groupSignatureRowsByDocument(data ?? []),
		[data]
	);
	// "Sign all" covers the bulk-eligible subset only. Witness-requiring documents are
	// excluded (bulk cannot collect a nomination) and are signed via their per-row Sign
	// action, which routes to the single-document flow that does.
	// Deduped: `documents` is one row per signature, so a signer holding two slots on one
	// document appears twice and would otherwise be counted — and submitted — twice.
	const bulkSignableInstanceIds = useMemo(
		() => [
			...new Set(
				documents
					.filter((document) => document.canBulkSign)
					.map((document) => document.documentWorkflowInstanceId)
			),
		],
		[documents]
	);
	const isOverBatchLimit = bulkSignableInstanceIds.length > MAX_BULK_SIGN_BATCH_SIZE;

	// Given the live selectable set, so the counter, the cap and the request can never
	// disagree about what is ticked.
	const selection = useProgrammeSigningSelection(bulkSignableInstanceIds);
	const { clear: clearSelection } = selection;

	// Selection mode is offered only above the batch size (see `isOverBatchLimit`), where the
	// signatory has to sign in rounds and so needs a say in which documents go first.
	const [isSelecting, setIsSelecting] = useState(false);

	const exitSelectionMode = useCallback(() => {
		clearSelection();
		setIsSelecting(false);
	}, [clearSelection]);

	const refreshDocuments = useCallback(
		() =>
			void queryClient.invalidateQueries({
				queryKey: PROGRAMME_SIGNING_DOCUMENTS_QUERY_KEY(token),
			}),
		[queryClient, token]
	);

	const bulkSign = usePublicBulkEidEasySign({
		token,
		selectableInstanceIds: bulkSignableInstanceIds,
		// The selection named something this signatory can no longer bulk sign, so the list has
		// moved under them. Refetching is enough: the selection prunes itself against the new
		// list, so the ticks that are still valid survive and only the dead one is dropped.
		onSelectionRejected: refreshDocuments,
		onComplete: () => {
			// The signatory starts again from the refreshed list: what they just signed is gone
			// from it, so the old selection is stale and the remaining backlog may now fit in
			// one batch — in which case the header goes back to a single "Sign all".
			exitSelectionMode();
			refreshDocuments();
		},
	});

	// One leading row and one checkbox per document, even when the signatory holds several
	// signature slots on it.
	const slotRoles = useMemo(() => buildDocumentSlotRoles(documents), [documents]);

	// Instances, not signature rows — same per-signature duplication as above.
	const witnessOnlyCount = useMemo(
		() =>
			new Set(
				documents
					.filter((document) => document.canSign && !document.canBulkSign)
					.map((document) => document.documentWorkflowInstanceId)
			).size,
		[documents]
	);

	// Tracked as state (rather than reading `tokenExchange.isError`) so the
	// single-document branch below can leave its loader — a failed exchange
	// there would otherwise spin forever with only an unhandled rejection.
	// A 403 means the admin cancelled this signatory between page load and the
	// click, so it gets the cancelled screen rather than the generic failure.
	const [exchangeFailure, setExchangeFailure] = useState<'cancelled' | 'error' | null>(null);

	// The row whose document is open in the preview modal (null = closed).
	const [previewRow, setPreviewRow] = useState<ProgrammeSigningDocumentRow | null>(null);
	const preview = useProgrammeSigningDocumentPreview(token, previewRow?.signatureId ?? '');

	// A failed preview fetch closes the modal (the cache-level handler already
	// toasts the error); leaving it open would show an empty viewer forever
	// since the query doesn't retry. The errored query is dropped too, so
	// re-opening the same row starts a fresh fetch instead of instantly
	// re-closing from the cached error.
	useEffect(() => {
		if (preview.isError && previewRow) {
			queryClient.removeQueries({
				queryKey: PROGRAMME_SIGNING_DOCUMENT_QUERY_KEY(token, previewRow.signatureId),
			});
			setPreviewRow(null);
		}
	}, [preview.isError, previewRow, queryClient, token]);

	const exchangeAndNavigate = useCallback(
		(signatureId: string, { withPortalReturn = false } = {}) => {
			tokenExchange
				.mutateAsync({ signatureId })
				.then(({ token: perSignatureToken }) => {
					if (withPortalReturn) {
						// Lets the single-doc page offer a "back to your documents"
						// button (see `getProgrammeReturnToken`). Omitted for the
						// single-document auto-redirect below — there is no table
						// to go back to, and the portal would just bounce forward.
						navigate(buildSingleDocPath(perSignatureToken), {
							state: { programmeToken: token },
						});
						return;
					}
					navigate(buildSingleDocPath(perSignatureToken));
				})
				.catch((error: unknown) => {
					setExchangeFailure(isForbiddenApiError(error) ? 'cancelled' : 'error');
				});
		},
		[tokenExchange, navigate, token]
	);

	// A single-document programme resolves straight to the existing single-doc
	// landing (full method picker) rather than the table + bulk sign-all UI.
	// Guarded so the exchange only ever fires once per mount, even though
	// `exchangeAndNavigate` is recreated on every render (the mutation object
	// from `useMutation` isn't referentially stable).
	const hasExchangedSingleDocRef = useRef(false);
	useEffect(() => {
		if (documents.length !== 1 || hasExchangedSingleDocRef.current) {
			return;
		}
		hasExchangedSingleDocRef.current = true;
		exchangeAndNavigate(documents[0].signatureId);
	}, [documents, exchangeAndNavigate]);

	const dataOperations = useMantineTableDataOperations();

	const columns = useMemo(
		() =>
			createProgrammeSigningColumns({
				onSignRow: (row) =>
					exchangeAndNavigate(row.signatureId, { withPortalReturn: true }),
				onViewRow: setPreviewRow,
				selection: isSelecting ? selection : undefined,
				slotRoles,
				tMipManage,
			}),
		[tMipManage, exchangeAndNavigate, isSelecting, selection, slotRoles]
	);

	if (exchangeFailure === 'cancelled') {
		return <ProgrammeSigningCancelledScreen />;
	}

	if (exchangeFailure === 'error') {
		return (
			<Flex direction="column" h="100vh">
				<PublicSigningHeader />
				<StatusScreen direction="column" align="center" justify="center" gap="md">
					<Icon variant="warning" size="3xl" />
					<Text c="red">
						{tMipManage('documents_page.programme_signing.exchange_error', {
							defaultValue:
								'Something went wrong preparing your document for signing.',
						})}
					</Text>
				</StatusScreen>
			</Flex>
		);
	}

	if (!token || isLoading || documents.length === 1) {
		return (
			<Flex direction="column" h="100vh">
				<PublicSigningHeader />
				<StatusScreen direction="column" align="center" justify="center" gap="md">
					<Loader size="lg" />
				</StatusScreen>
			</Flex>
		);
	}

	if (isError) {
		return (
			<Flex direction="column" h="100vh">
				<PublicSigningHeader />
				<StatusScreen direction="column" align="center" justify="center" gap="md">
					<Icon variant="warning" size="3xl" />
					<Text c="red">
						{tMipManage('documents_page.programme_signing.error', {
							defaultValue: 'Something went wrong loading your documents.',
						})}
					</Text>
				</StatusScreen>
			</Flex>
		);
	}

	if (documents.length === 0) {
		return <ProgrammeSigningCancelledScreen />;
	}

	return (
		<Flex direction="column" h="100vh">
			<PublicSigningHeader />
			<Flex align="center" justify="space-between" px="xl" py="md">
				<Text fw={600} size="xl">
					{tMipManage('documents_page.programme_signing.title', {
						defaultValue: 'Your documents',
					})}
				</Text>
				<Flex align="center" gap="md">
					{isSelecting && (
						<Text size="sm">
							{tMipManage('documents_page.programme_signing.selection_counter', {
								count: selection.selectedCount,
								max: MAX_BULK_SIGN_BATCH_SIZE,
								defaultValue: '{{count}} of {{max}} selected',
							})}
						</Text>
					)}
					{witnessOnlyCount > 0 && (
						<Text size="sm">
							{tMipManage('documents_page.programme_signing.witness_excluded', {
								count: witnessOnlyCount,
							})}
						</Text>
					)}
					{isSelecting && (
						<>
							<Button variant="default" onClick={exitSelectionMode}>
								{tMipManage(
									'documents_page.programme_signing.selection_cancel_button',
									{ defaultValue: 'Cancel' }
								)}
							</Button>
							<Button
								onClick={() =>
									bulkSign.startBulkSign(selection.selectedInstanceIds, {
										isExplicitSelection: true,
									})
								}
								disabled={selection.selectedCount === 0}
							>
								{tMipManage(
									'documents_page.programme_signing.sign_selected_button',
									{
										count: selection.selectedCount,
										defaultValue: 'Sign {{count}} selected',
									}
								)}
							</Button>
						</>
					)}
					{!isSelecting && isOverBatchLimit && (
						<Button onClick={() => setIsSelecting(true)}>
							{tMipManage('documents_page.programme_signing.sign_multiple_button', {
								defaultValue: 'Sign multiple',
							})}
						</Button>
					)}
					{!isSelecting && !isOverBatchLimit && (
						<Button
							onClick={() => bulkSign.startBulkSign(bulkSignableInstanceIds)}
							disabled={bulkSignableInstanceIds.length === 0}
						>
							{tMipManage('documents_page.programme_signing.sign_all_button', {
								defaultValue: 'Sign all',
							})}
						</Button>
					)}
				</Flex>
			</Flex>
			<Flex
				direction="column"
				px="xl"
				pb="xl"
				style={{ flex: 1, minHeight: 0, overflow: 'auto' }}
			>
				<DocumentTable<ProgrammeSigningDocumentRow>
					data={documents}
					columns={columns}
					dataOperations={dataOperations}
					// Per signature, not per document: the list is one row per signature, so keying
					// on the instance id gives two rows the same React key (and the same hover
					// identity) whenever a signatory holds several slots on one document.
					getRowId={(row) => row.signatureId}
					enablePagination={false}
					enableRowSelection={false}
					hasNoTopToolbar
					enableTopToolbar={false}
					manualSorting={false}
				/>
			</Flex>
			{previewRow && (
				<Suspense fallback={null}>
					<DocumentViewerModalV2
						opened
						onClose={() => setPreviewRow(null)}
						file={preview.data}
						isLoading={preview.isLoading}
						fileName={previewRow.documentName}
						fileExtension="pdf"
					/>
				</Suspense>
			)}
			<EidEasySigningModal
				status={bulkSign.status}
				challengeCode={bulkSign.challengeCode}
				error={bulkSign.error}
				isSigningInProgress={bulkSign.isSigningInProgress}
				isModalOpen={bulkSign.isModalOpen}
				onClose={bulkSign.reset}
				onSelectMethod={bulkSign.selectMethod}
				onGoBack={bulkSign.goBack}
				onConfirmSignature={bulkSign.confirmSignature}
				onSignatureConfirmed={bulkSign.confirmSignature}
				onConfirmOtp={bulkSign.confirmOtp}
				onSubmitOtpCode={bulkSign.submitOtpCode}
				onResendOtpCode={bulkSign.resendOtpCode}
				otpVerifying={bulkSign.otpVerifying}
				otpCodeError={bulkSign.otpCodeError}
				otpChannel={bulkSign.otpChannel}
				otpMaskedContact={bulkSign.otpMaskedContact}
				allowedMethods={PUBLIC_BULK_ALLOWED_METHODS}
				// Authorised signatories are unauthenticated; the saved-signature
				// picker's /user/signature query would 401 (and redirect to login).
				disableSavedSignature
				progress={bulkSign.progress}
			/>
		</Flex>
	);
};
